import type {
  EventViewPayload,
  MergeVisitorPayload,
  NewsViewPayload,
} from '@/lib/api/analytics/types';
import { getVisitorId } from '@/lib/api/analytics/visitor';
import { readXsrfCookie } from '@/lib/api/httpClient';

const BASE_URL = process.env.NEXT_PUBLIC_API_ANALYTICS_URL ?? '';

const EVENT_VIEW_PATH = '/api/analytics/event-view';
const NEWS_VIEW_PATH = '/api/analytics/news-view';
const MERGE_VISITOR_PATH = '/api/analytics/merge-visitor';

export function isAnalyticsEnabled(): boolean {
  return BASE_URL.length > 0;
}

function buildHeaders(): Record<string, string> {
  const xsrf = readXsrfCookie();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (xsrf) {
    headers['X-XSRF-TOKEN'] = xsrf;
  }
  return headers;
}

async function postView(path: string, body: EventViewPayload | NewsViewPayload): Promise<void> {
  if (!isAnalyticsEnabled()) return;
  try {
    await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: buildHeaders(),
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Analytics must never affect UX — swallow all errors.
  }
}

function beaconView(path: string, body: EventViewPayload | NewsViewPayload): void {
  if (!isAnalyticsEnabled()) return;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  try {
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    navigator.sendBeacon(`${BASE_URL}${path}`, blob);
  } catch {
    // ignore
  }
}

export function trackEventView(payload: EventViewPayload): Promise<void> {
  return postView(EVENT_VIEW_PATH, payload);
}

export function trackNewsView(payload: NewsViewPayload): Promise<void> {
  return postView(NEWS_VIEW_PATH, payload);
}

export function beaconEventView(payload: EventViewPayload): void {
  beaconView(EVENT_VIEW_PATH, payload);
}

export function beaconNewsView(payload: NewsViewPayload): void {
  beaconView(NEWS_VIEW_PATH, payload);
}

// Client-side country resolution for pages that can't compute it server-side without
// opting out of ISR (e.g. news articles). Resolved once per browser session via the
// existing geolocation route (single-origin: routes to the web zone from anywhere).
let countryPromise: Promise<string | undefined> | null = null;

export function resolveViewerCountry(): Promise<string | undefined> {
  if (countryPromise) return countryPromise;
  if (typeof fetch === 'undefined') return Promise.resolve(undefined);
  countryPromise = fetch('/api/geolocation')
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => (data && typeof data.country === 'string' ? data.country : undefined))
    .catch(() => undefined); // country is optional — never surface errors.
  return countryPromise;
}

// `setAuthData` is the single chokepoint for all auth flows, but it also fires on
// profile updates / token refresh — this guard keeps merge to once per login.
// resetVisitorMerge() re-arms it on logout so the next login merges again.
let hasMerged = false;

export function resetVisitorMerge(): void {
  hasMerged = false;
}

/**
 * Attach the anonymous `visitor_id` history to the just-authenticated account.
 * Fire-and-forget and idempotent server-side. No-op when analytics is disabled,
 * already merged this session, or no visitor cookie exists.
 */
export function mergeVisitorAfterLogin(): void {
  if (!isAnalyticsEnabled()) return;
  if (hasMerged) return;
  const visitorId = getVisitorId();
  if (!visitorId) return;

  hasMerged = true;
  const body: MergeVisitorPayload = { visitorId };
  try {
    void fetch(`${BASE_URL}${MERGE_VISITOR_PATH}`, {
      method: 'POST',
      credentials: 'include',
      headers: buildHeaders(),
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // Never block the login flow.
    });
  } catch {
    // Analytics must never affect UX — swallow all errors.
  }
}
